// Drawer displaying Consistency intelligence for a document.
import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import { DataTable } from "../../../components/DataTable";
import { SectionCard, StatTile } from "../Domain";
import { formatPercent } from "../utils";
import type {
  ConsistencyDisplayData,
  ConsistencyResult,
} from "./utilities/types";
import {
  ConsistencyEmptyState,
  ConsistencyHeader,
  ConsistencyLoading,
  ContradictionAlert,
  DefinitionMismatchCard,
  PrimaryConsistencyHighlight,
} from "./parts";
import {
  adaptConsistencyResult,
  sortFindingsBySeverity,
} from "./utilities/helpers";
import {
  contradictionColumns,
  findingColumns,
  referenceColumns,
  terminologyColumns,
} from "./utilities/columns";
import {
  CONSISTENCY_DRAWER_WIDTH_CLS,
  CONSISTENCY_EMPTY_MESSAGES,
  CONSISTENCY_ITEM_LABELS,
  CONSISTENCY_SECTION_LABELS,
  CONSISTENCY_STAT_TONES,
  CONSISTENCY_TABLE_MAX_HEIGHT,
  CONSISTENCY_TABLE_PAGE_SIZE,
  CONSISTENCY_COPY,
} from "./utilities/constants";

export interface ConsistencyDrawerProps {
  open: boolean;
  onClose: () => void;
  result?: ConsistencyResult | null;
  documentTitle?: string;
  documentType?: string;
}

export function ConsistencyDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
}: ConsistencyDrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="z-[80]"
        className={`z-[80] flex h-full ${CONSISTENCY_DRAWER_WIDTH_CLS} flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]`}
      >
        <SheetTitle className="sr-only">{CONSISTENCY_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {CONSISTENCY_COPY.sheetDescription}
        </SheetDescription>
        {!result ? (
          <ConsistencyLoading />
        ) : (
          <ConsistencyContent
            /* Remounting on document change resets paging without a useEffect. */
            key={result.documentId ?? "no-doc"}
            raw={result}
            documentTitle={documentTitle}
            documentType={documentType}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface ConsistencyContentProps {
  raw: ConsistencyResult;
  documentTitle?: string;
  documentType?: string;
}

function ConsistencyContent({
  raw,
  documentTitle,
  documentType,
}: ConsistencyContentProps) {
  // ── Local UI state ──────────────────────────────────────────────
  const [findingsPage, setFindingsPage] = useState(0);
  const [termPage, setTermPage] = useState(0);
  const [refPage, setRefPage] = useState(0);

  // ── Derived data (raw → display) ────────────────────────────────
  const data: ConsistencyDisplayData = useMemo(
    () => adaptConsistencyResult(raw, documentTitle, documentType),
    [raw, documentTitle, documentType],
  );

  const {
    summary,
    internal_contradictions: contradictions,
    terminology,
    definition_mismatches: definitionMismatches,
    reference_gaps: referenceGaps,
    findings,
    backendNote,
  } = data;

  const overallPct = formatPercent(summary.consistency_score);
  const sortedFindings = sortFindingsBySeverity(findings);
  const highSeverityCount = findings.filter((f) => f.severity === "HIGH").length;
  const showContradictionAlert =
    summary.contradictions_detected > 0 || highSeverityCount > 0;

  return (
    <>
      <ConsistencyHeader
        document={data.document}
        documentTitle={documentTitle}
        documentType={documentType}
        backendNote={backendNote}
        overallPct={overallPct}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
        {showContradictionAlert && (
          <ContradictionAlert
            contradictionsDetected={summary.contradictions_detected}
          />
        )}

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.summary.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.summary.right}
        >
          <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-5">
            <StatTile
              value={summary.statements_analysed}
              label="Statements analysed"
              tone={CONSISTENCY_STAT_TONES.statements}
            />
            <StatTile
              value={summary.contradictions_detected}
              label="Contradictions detected"
              tone={CONSISTENCY_STAT_TONES.contradictions}
            />
            <StatTile
              value={summary.terminology_variants}
              label="Terminology variants"
              tone={CONSISTENCY_STAT_TONES.terminology}
            />
            <StatTile
              value={summary.unresolved_references}
              label="Unresolved references"
              tone={CONSISTENCY_STAT_TONES.references}
            />
            <StatTile
              value={overallPct}
              label="Consistency score"
              tone={CONSISTENCY_STAT_TONES.score}
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.highlight.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.highlight.right}
        >
          <PrimaryConsistencyHighlight
            overallPct={overallPct}
            contradictions={summary.contradictions_detected}
            terminology={summary.terminology_variants}
            references={summary.unresolved_references}
            definitions={summary.definition_mismatches}
            findings={summary.findings}
          />
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.contradictions.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.contradictions.right(
            contradictions.length,
          )}
        >
          <div className="p-3">
            {contradictions.length === 0 ? (
              <ConsistencyEmptyState
                message={CONSISTENCY_EMPTY_MESSAGES.contradictions}
              />
            ) : (
              <DataTable
                columns={contradictionColumns}
                data={contradictions}
                compact
                maxHeight={CONSISTENCY_TABLE_MAX_HEIGHT}
                rowKey={(row, i) =>
                  row.contradiction_id ?? `contradiction-${i}`
                }
              />
            )}
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.terminology.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.terminology.right(
            terminology.length,
          )}
        >
          <div className="p-3">
            <DataTable
              columns={terminologyColumns}
              data={terminology}
              compact
              maxHeight={CONSISTENCY_TABLE_MAX_HEIGHT}
              page={termPage}
              pageSize={CONSISTENCY_TABLE_PAGE_SIZE}
              totalItems={terminology.length}
              onPageChange={setTermPage}
              itemLabel={CONSISTENCY_ITEM_LABELS.terminology}
              pagerStyle="simple"
              rowKey={(row, i) => row.term ?? `term-${i}`}
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.definitionMismatches.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.definitionMismatches.right(
            definitionMismatches.length,
          )}
        >
          <div className="flex flex-col gap-3 p-4">
            {definitionMismatches.length === 0 ? (
              <ConsistencyEmptyState
                message={CONSISTENCY_EMPTY_MESSAGES.definitionMismatches}
              />
            ) : (
              definitionMismatches.map((d, i) => (
                <DefinitionMismatchCard
                  key={d.concept ?? `dm-${i}`}
                  item={d}
                />
              ))
            )}
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.referenceGaps.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.referenceGaps.right(
            referenceGaps.length,
          )}
        >
          <div className="p-3">
            <DataTable
              columns={referenceColumns}
              data={referenceGaps}
              compact
              maxHeight={CONSISTENCY_TABLE_MAX_HEIGHT}
              page={refPage}
              pageSize={CONSISTENCY_TABLE_PAGE_SIZE}
              totalItems={referenceGaps.length}
              onPageChange={setRefPage}
              itemLabel={CONSISTENCY_ITEM_LABELS.references}
              pagerStyle="simple"
              rowKey={(row, i) =>
                `${row.reference_type ?? "ref"}-${row.referenced_item ?? i}`
              }
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={CONSISTENCY_SECTION_LABELS.findings.header}
          rightLabel={CONSISTENCY_SECTION_LABELS.findings.right(findings.length)}
        >
          <div className="p-3">
            <DataTable
              columns={findingColumns}
              data={sortedFindings}
              compact
              maxHeight={CONSISTENCY_TABLE_MAX_HEIGHT}
              page={findingsPage}
              pageSize={CONSISTENCY_TABLE_PAGE_SIZE}
              totalItems={findings.length}
              onPageChange={setFindingsPage}
              itemLabel={CONSISTENCY_ITEM_LABELS.findings}
              pagerStyle="simple"
              rowKey={(row, i) =>
                `${row.finding_type ?? "finding"}-${row.affected_area ?? i}`
              }
            />
          </div>
        </SectionCard>
      </div>
    </>
  );
}
